import i18n from 'i18next'
import { Crop } from 'react-image-crop'
import { toInteger } from 'lodash'
import Fuse, { FuseOptionKey } from 'fuse.js'
import { green, amber, purple, teal, grey, deepOrange } from '@mui/material/colors'

/**
 * Converts a data URL to a File object.
 *
 * @param {string | ArrayBuffer} dataurl - The data URL to convert.
 * @param {string} filename - The name of the file.
 * @return {File} The converted File object.
 */
const dataURLtoFile = (dataurl: string | ArrayBuffer, filename: string): File => {
  const arr = dataurl.toString().split(',')
  if (arr.length < 2) {
    throw new Error('Invalid dataurl')
  }

  const match = arr[0].match(/:(.*?);/)
  if (!match) {
    throw new Error('Invalid dataurl mime part')
  }

  const mime = match[1],
    bstr = atob(arr[1])

  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

/**
 * Returns an array of language levels.
 *
 * @return {Array} An array containing objects representing language levels.
 */
export const languageLevels = () => {
  return [
    {
      level: 1,
      label: `A2 - ${i18n.t('language.basic')}`,
      description: i18n.t('language.description.a2'),
    },
    {
      level: 2,
      label: `B1 - ${i18n.t('language.moderate')}`,
      description: i18n.t('language.description.b1'),
    },
    {
      level: 3,
      label: `B2 - ${i18n.t('language.good')}`,
      description: i18n.t('language.description.b2'),
    },
    {
      level: 4,
      label: `C1 - ${i18n.t('language.excellent')}`,
      description: i18n.t('language.description.c1'),
    },
    {
      level: 5,
      label: `C2 - ${i18n.t('language.native')}`,
      description: i18n.t('language.description.c2'),
    },
  ]
}

/**
 * Returns an array of organization search types.
 *
 * @return {Array} An array containing objects with the properties `value` and `label`.
 */
export const organizationSearchTypes = () => {
  return [
    {
      value: 'SEARCH_USERS_ORGANIZATIONS',
      label: `${i18n.t('resources.searchEmployees.userOrganizations')}`,
    },
    {
      value: 'SEARCH_NETWORK_ORGANIZATIONS',
      label: `${i18n.t('resources.searchEmployees.networkOrganizations')}`,
    },
    {
      value: 'SEARCH_ALL_ORGANIZATIONS',
      label: `${i18n.t('resources.searchEmployees.allOrganizations')}`,
    },
  ]
}

export interface ISkillType {
  value: string
  label: string
}

/**
 * Returns an array of skill types.
 *
 * @return {Array} An array of objects representing different skill types.
 */
export const skillTypes = () => {
  return [
    {
      value: 'skill',
      label: `${i18n.t('reports.skillsStatistics.type.skills')}`,
    },
    {
      value: 'industry',
      label: `${i18n.t('reports.skillsStatistics.type.industries')}`,
    },
    {
      value: 'role',
      label: `${i18n.t('reports.skillsStatistics.type.roles')}`,
    },
  ]
}

/**
 * Retrieves the skill levels based on the specified type.
 *
 * @param {string} type - Optional. The type of skill levels to retrieve. Valid values are 'skill', 'industry', or 'role'.
 * @return {Array} An array of skill level objects, each containing a level, label, and description.
 */
export const skillLevels = (type?: 'skill' | 'industry' | 'role') => {
  return [
    {
      level: 1,
      label: `${i18n.t('profile.skill.fundamental')}`,
      description: `${i18n.t(`profile.${type ?? 'skill'}.fundamental.description`)}`,
    },
    {
      level: 2,
      label: `${i18n.t('profile.skill.novice')}`,
      description: `${i18n.t(`profile.${type ?? 'skill'}.novice.description`)}`,
    },
    {
      level: 3,
      label: `${i18n.t('profile.skill.intermediate')}`,
      description: `${i18n.t(`profile.${type ?? 'skill'}.intermediate.description`)}`,
    },
    {
      level: 4,
      label: `${i18n.t('profile.skill.advanced')}`,
      description: `${i18n.t(`profile.${type ?? 'skill'}.advanced.description`)}`,
    },
    {
      level: 5,
      label: `${i18n.t('profile.skill.expert')}`,
      description: `${i18n.t(`profile.${type ?? 'skill'}.expert.description`)}`,
    },
  ]
}

/**
 * Returns an array of skill interest levels.
 *
 * @return {Array} An array of skill interest levels.
 */
export const skillInterestLevels = () => {
  return [
    {
      level: 1,
      label: `${i18n.t('profile.skill.interest.low')}`,
    },
    {
      level: 2,
      label: `${i18n.t('profile.skill.interest.slight')}`,
    },
    {
      level: 3,
      label: `${i18n.t('profile.skill.interest.medium')}`,
    },
    {
      level: 4,
      label: `${i18n.t('profile.skill.interest.high')}`,
    },
    {
      level: 5,
      label: `${i18n.t('profile.skill.interest.veryHigh')}`,
    },
  ]
}

/**
 * Generates an array of allocation states.
 *
 * @return {Array} An array of objects representing allocation states.
 */
export const allocationStates = () => {
  return [
    {
      state: 0,
      label: `${i18n.t('allocation.state.0')}`,
    },
    {
      state: 1,
      label: `${i18n.t('allocation.state.1')}`,
    },
  ]
}

/**
 * Returns an array of admin permission group types.
 *
 * @return {Array} An array of objects representing the different admin permission group types.
 */
export const adminPermissionGroupTypes = () => {
  return [
    {
      value: 'admin',
      label: i18n.t('admin.permission.groupType.admin'),
    },
    {
      value: 'inputDataManager',
      label: i18n.t('admin.permission.groupType.inputDataManager'),
    },
    {
      value: 'companyAdmin',
      label: i18n.t('admin.permission.groupType.companyAdmin'),
    },
    {
      value: 'sales',
      label: i18n.t('admin.permission.groupType.sales'),
    },
    {
      value: 'freelancer',
      label: i18n.t('admin.permission.groupType.freelancer'),
    },
    {
      value: 'recruit',
      label: i18n.t('admin.permission.groupType.recruit'),
    },
    {
      value: 'teamLeader',
      label: i18n.t('admin.permission.groupType.teamLeader'),
    },
  ]
}

/**
 * Retrieves a cropped image from the provided image based on the given crop dimensions.
 *
 * @param {Crop} crop - The crop dimensions specifying the area of the image to be cropped.
 * @param {HTMLImageElement} image - The image from which to retrieve the cropped image.
 * @return {Promise<File | undefined>} A promise that resolves to the cropped image file, or undefined if the crop fails.
 */
export async function getCroppedImg(crop: Crop, image: HTMLImageElement): Promise<File | undefined> {
  const wanted = {
    width: Math.min(220, image.width, image.height),
    height: Math.min(220, image.width, image.height),
  }

  if (!crop.width || !crop.height || crop.x === undefined || crop.y === undefined) {
    throw 'Bad crop'
  }

  const canvas = document.createElement('canvas')
  canvas.width = wanted.width
  canvas.height = wanted.height

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw 'Canvas context not supported'
  }

  ctx.drawImage(
    image,
    (crop.x / 100) * image.width,
    (crop.y / 100) * image.height,
    (crop.width / 100) * image.width,
    (crop.height / 100) * image.height,
    0,
    0,
    canvas.width,
    canvas.height
  )

  const reader = new FileReader()

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        return reject('No blob')
      }

      reader.onload = () => {
        if (!reader.result) {
          return reject('No result buffer!')
        }
        resolve(dataURLtoFile(reader.result, 'cropped.png'))
      }
      reader.onerror = err => {
        reject(`Error processing crop to file: ${err}`)
      }
      reader.readAsDataURL(blob)
    }, 'image/png')
  })
}

/**
 * Converts a given date to a display date based on the specified resolution.
 *
 * @param {Date | number | string} date - The date to be converted.
 * @param {string} [resolution='day'] - The resolution of the display date. Defaults to 'day'.
 * @return {string} - The converted display date.
 */
export const convertToDisplayDate = (date: Date | number | string | null | undefined, resolution = 'day'): string => {
  if (date === null || typeof date === 'undefined' || date === '') {
    return ''
  }

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  let locale: string

  switch (i18n.language) {
    case 'fi':
      locale = 'fi-FI'
      break

    case 'en':
      locale = 'en-GB'
      break

    case 'sv':
      locale = 'sv-SE'
      break

    default:
      locale = 'fi-FI'
      break
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: resolution === 'month' || resolution === 'day' || resolution === 'time' ? 'numeric' : undefined,
    day: resolution === 'day' || resolution === 'time' ? 'numeric' : undefined,
    hour: resolution === 'time' ? '2-digit' : undefined,
    minute: resolution === 'time' ? '2-digit' : undefined,
  }).format(parsedDate)
}

/**
 * Returns the date format based on the given locale and resolution.
 *
 * @param {string} locale - The locale to determine the date format for.
 * @param {string} [resolution='day'] - The resolution of the date format.
 * @return {string} The date format based on the locale and resolution.
 */
export const getDateFormatByLocaleAndResolution = (locale: string, resolution = 'day') => {
  const days = 'dd'
  const months = 'MM'
  const years = 'yyyy'

  switch (resolution) {
    case 'year':
      return years
    case 'month':
      if (locale === 'fi' || locale === 'en') {
        return `${months}.${years}`
      } else if (locale === 'sv') {
        return `${years}-${months}`
      } else {
        return `${months}.${years}`
      }
    case 'day':
    default:
      if (locale === 'fi' || locale === 'en') {
        return `${days}.${months}.${years}`
      } else if (locale === 'sv') {
        return `${years}-${months}-${days}`
      } else {
        return `${days}.${months}.${years}`
      }
  }
}

/**
 * Sets the individual values of a Date object.
 *
 * @param {Date} date - The Date object to modify.
 * @param {number} year - The year value to set.
 * @param {number} month - The month value (0-11) to set.
 * @param {number} day - The day value (1-31) to set.
 * @param {number} hours - The hours value (0-23) to set.
 * @param {number} minutes - The minutes value (0-59) to set.
 * @param {number} seconds - The seconds value (0-59) to set.
 * @param {number} millis - The milliseconds value (0-999) to set.
 */
const setDateValues = (
  date: Date,
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  seconds: number,
  millis: number
) => {
  date.setFullYear(year)
  date.setMonth(month)
  date.setDate(day)
  date.setHours(hours)
  date.setMinutes(minutes)
  date.setSeconds(seconds)
  date.setMilliseconds(millis)
}

/**
 * Formats the end date based on the given resolution.
 *
 * @param {Date} date - The date to format.
 * @param {string} resolution - The resolution of the date formatting. Defaults to 'day'.
 * @return {Date} - The formatted end date.
 */
export const formatEndDate = (date: Date, resolution = 'day'): Date => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = resolution === 'year' || resolution === 'month' ? 0 : date.getDate()

  if (resolution === 'year') setDateValues(date, year, 12, day, 23, 59, 59, 0)
  else if (resolution === 'month') setDateValues(date, year, month + 1, day, 23, 59, 59, 0)
  else setDateValues(date, year, month, day, 23, 59, 59, 0)

  return date
}

/**
 * Formats the start date based on the given resolution.
 *
 * @param {Date} date - The date to be formatted.
 * @param {string} resolution - The resolution to format the date. Defaults to 'day'.
 * @return {Date} - The formatted date.
 */
export const formatStartDate = (date: Date, resolution = 'day'): Date => {
  const year = date.getFullYear()
  const month = date.getMonth()

  const day = resolution === 'year' || resolution === 'month' ? 1 : date.getDate()

  if (resolution === 'year') {
    setDateValues(date, year, 0, day, 0, 0, 0, 0)
  } else {
    setDateValues(date, year, month, day, 0, 0, 0, 0)
  }

  return date
}

// Fuzzy matching is only a fallback for typos. Queries this short or shorter skip it,
// because a fuzzy match on a few letters is mostly noise ("was" -> "AWS", "SAS").
const FUZZY_MIN_QUERY_LENGTH = 4

// Match tiers, best first. An option gets the best tier of any of its searched values
enum MatchTier {
  Exact,
  Prefix,
  Word,
  Acronym,
  WordPrefix,
  Substring,
}

interface NormalizedSearchValue {
  value: string
  words: string[]
  acronym: string
}

interface FuseCacheEntry {
  fuse: Fuse<unknown>
  normalized: NormalizedSearchValue[][]
}

// Cache for Fuse instances and normalized option values to improve performance
// Structure: WeakMap<options array reference, Map<stringified keys, cache entry>>
// This ensures different arrays (skills, certificates, competences) each get their own
// Fuse instance, even if they use the same search keys like ['translations.name']
const fuseCache = new WeakMap<object, Map<string, FuseCacheEntry>>()

const normalizeSearchText = (text: string): string => text.trim().toLowerCase()

const normalizeSearchValue = (text: string): NormalizedSearchValue => {
  const value = normalizeSearchText(text)
  const words = value.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  return { value, words, acronym: words.map(word => word[0]).join('') }
}

const getKeyValues = <T>(option: T, key: FuseOptionKey<T>): string[] => {
  let values: ReadonlyArray<string> | string | undefined
  if (typeof key === 'string' || Array.isArray(key)) {
    values = Fuse.config.getFn(option, key)
  } else {
    values = key.getFn ? key.getFn(option) : Fuse.config.getFn(option, key.name)
  }
  if (values === undefined || values === null) {
    return []
  }
  return typeof values === 'string' ? [values] : [...values]
}

const getMatchTier = (values: NormalizedSearchValue[], query: string): MatchTier | undefined => {
  let best: MatchTier | undefined
  for (const { value, words, acronym } of values) {
    let tier: MatchTier | undefined
    if (value === query) {
      return MatchTier.Exact
    } else if (value.startsWith(query)) {
      tier = MatchTier.Prefix
    } else if (words.includes(query)) {
      tier = MatchTier.Word
    } else if (acronym.length > 1 && acronym.startsWith(query)) {
      tier = MatchTier.Acronym
    } else if (words.some(word => word.startsWith(query))) {
      tier = MatchTier.WordPrefix
    } else if (value.includes(query)) {
      tier = MatchTier.Substring
    }
    if (tier !== undefined && (best === undefined || tier < best)) {
      best = tier
    }
  }
  return best
}

// Shortest matching value, so prefix matches list "Java" before "JavaScript"
const getShortestPrefixLength = (values: NormalizedSearchValue[], query: string): number =>
  Math.min(...values.filter(({ value }) => value.startsWith(query)).map(({ value }) => value.length))

/**
 * Performs filtering on an array of options, ranking real matches first:
 * exact name, name prefix, whole word, acronym ("ckad"), word prefix, substring, and last
 * Fuse.js fuzzy matches for typos. Ties keep the original option order, except that prefix
 * matches list shorter names first. Every searched value counts (e.g. both fi and en names).
 *
 * Caches the Fuse instance and the normalized option values based on the options array
 * reference and keys to avoid recreating them on every call. Each unique options array gets
 * its own cache, so different arrays (e.g., skills vs competences) won't share them even
 * with identical keys.
 *
 * @param {T[]} options - The array of options to filter.
 * @param {string} inputValue - The input value to filter by.
 * @param {FuseOptionKey<T>[]} keys - The keys to search within the options.
 * @return {T[]} The filtered array of options.
 */
export const fuseFiltering = <T>(options: T[], inputValue: string, keys: FuseOptionKey<T>[]): T[] => {
  const query = normalizeSearchText(inputValue ?? '')
  if (!query.length) {
    return options
  }

  // Create a cache key from the keys array
  const cacheKey = JSON.stringify(keys)

  // Get or create the cache for this specific options array (by reference)
  let optionsCache = fuseCache.get(options)
  if (!optionsCache) {
    optionsCache = new Map()
    fuseCache.set(options, optionsCache)
  }

  // Get or create the Fuse instance and normalized values for this combination of options and keys
  let entry = optionsCache.get(cacheKey)
  if (!entry) {
    const fuseOptions = {
      includeScore: true,
      threshold: 0.4,
      ignoreLocation: true,
      keys: keys,
    }
    entry = {
      fuse: new Fuse<T>(options, fuseOptions) as unknown as Fuse<unknown>,
      normalized: options.map(option =>
        keys.flatMap(key => getKeyValues(option, key)).map(value => normalizeSearchValue(value))
      ),
    }
    optionsCache.set(cacheKey, entry)
  }
  const { fuse, normalized } = entry

  const matches: { index: number; tier: MatchTier; length: number }[] = []
  normalized.forEach((values, index) => {
    const tier = getMatchTier(values, query)
    if (tier !== undefined) {
      const length = tier === MatchTier.Prefix ? getShortestPrefixLength(values, query) : 0
      matches.push({ index, tier, length })
    }
  })
  matches.sort((a, b) => a.tier - b.tier || a.length - b.length || a.index - b.index)
  const ranked = matches.map(({ index }) => options[index])

  if (query.length < FUZZY_MIN_QUERY_LENGTH) {
    return ranked
  }

  const matched = new Set(matches.map(({ index }) => index))
  const fuzzy = (fuse as Fuse<T>)
    .search(query)
    .filter(result => !matched.has(result.refIndex))
    .map(result => result.item)
  return [...ranked, ...fuzzy]
}

// Characters ignored when comparing option names, the same as SKILL_NAME_SEPARATORS in caleo-back
// (+ and # are kept, so C, C# and C++ differ)
const OPTION_NAME_SEPARATORS = /[\s\-_./]+/g

/**
 * Normalizes an option name (skill, certificate, competence) for duplicate comparison, the same way
 * as the backend does when it creates options: lower case, without whitespace, -, _, . and /.
 *
 * @param {string} name - The option name.
 * @return {string} The normalized name.
 */
export const normalizeOptionName = (name: string): string => name.toLowerCase().replace(OPTION_NAME_SEPARATORS, '')

/**
 * Keeps one option per name, so duplicates like two "Scrum Master" certificates show as one picker
 * option. Names are compared with normalizeOptionName. In each group the kept option is the first of:
 * one in keepIds (already used or selected), a promoted one, the one with the lowest id.
 * Options without a name are all kept, and the original order is kept.
 *
 * @param {T[]} options - The options to collapse.
 * @param {(option: T) => string | undefined} getName - Returns the option's display name.
 * @param {number[]} keepIds - Ids to prefer when they have duplicates.
 * @return {T[]} The options without duplicates.
 */
export const collapseDuplicateOptions = <T extends { id: number; isPromoted?: boolean }>(
  options: T[],
  getName: (option: T) => string | undefined,
  keepIds: number[] = []
): T[] => {
  const keep = new Set(keepIds)
  const rank = (option: T) => [keep.has(option.id) ? 0 : 1, option.isPromoted ? 0 : 1, option.id]
  const isBetter = (a: T, b: T) => {
    const [rankA, rankB] = [rank(a), rank(b)]
    const index = rankA.findIndex((value, i) => value !== rankB[i])
    return index !== -1 && rankA[index] < rankB[index]
  }

  const keys = options.map(option => normalizeOptionName(getName(option) ?? ''))
  const best = new Map<string, T>()
  options.forEach((option, index) => {
    const key = keys[index]
    const current = best.get(key)
    if (key && (!current || isBetter(option, current))) {
      best.set(key, option)
    }
  })

  return options.filter((option, index) => !keys[index] || best.get(keys[index]) === option)
}

// Lowest trigram similarity counted as a similar option name, the same as the pg_trgm default
const SIMILAR_OPTION_MIN_SIMILARITY = 0.3
const SIMILAR_OPTION_LIMIT = 5

/**
 * Returns the trigrams of a text the same way as Postgres pg_trgm: lower case, each word (letters
 * and digits) padded with two spaces in front and one after.
 *
 * @param {string} text - The text.
 * @return {Set<string>} The trigrams.
 */
const getTrigrams = (text: string): Set<string> => {
  const trigrams = new Set<string>()
  for (const word of text.toLowerCase().split(/[^\p{L}\p{N}]+/u)) {
    if (!word) continue
    const padded = `  ${word} `
    for (let i = 0; i < padded.length - 2; i++) {
      trigrams.add(padded.slice(i, i + 3))
    }
  }
  return trigrams
}

const getTrigramSimilarity = (a: Set<string>, b: Set<string>): number => {
  if (!a.size || !b.size) return 0
  let shared = 0
  a.forEach(trigram => {
    if (b.has(trigram)) shared++
  })
  return shared / (a.size + b.size - shared)
}

/**
 * Trigram similarity of two texts, the same as Postgres pg_trgm similarity(): shared trigrams
 * divided by all distinct trigrams of both, from 0 to 1.
 *
 * @param {string} a - The first text.
 * @param {string} b - The second text.
 * @return {number} The similarity.
 */
export const trigramSimilarity = (a: string, b: string): number => getTrigramSimilarity(getTrigrams(a), getTrigrams(b))

/**
 * Finds existing options with a name close to a name the user is about to create, for a "Did you
 * mean" prompt. Every name of an option counts (all languages). Options whose name is the same apart
 * from case, spaces and punctuation (normalizeOptionName) come first, then the rest by trigram
 * similarity, highest first. Ties keep the original order.
 *
 * @param {T[]} options - The options to search.
 * @param {string} name - The name being created.
 * @param {(option: T) => string[]} getNames - Returns all names of an option.
 * @param {number} limit - The maximum number of options to return.
 * @return {T[]} The similar options, best first.
 */
export const findSimilarOptions = <T>(
  options: T[],
  name: string,
  getNames: (option: T) => string[],
  limit = SIMILAR_OPTION_LIMIT
): T[] => {
  const normalized = normalizeOptionName(name)
  const trigrams = getTrigrams(name)

  const matches: { option: T; score: number; index: number }[] = []
  options.forEach((option, index) => {
    const names = getNames(option).filter(Boolean)
    // A same-name match ranks above any similarity (at most 1)
    const score =
      normalized && names.some(optionName => normalizeOptionName(optionName) === normalized)
        ? 2
        : Math.max(0, ...names.map(optionName => getTrigramSimilarity(trigrams, getTrigrams(optionName))))
    if (score >= SIMILAR_OPTION_MIN_SIMILARITY) {
      matches.push({ option, score, index })
    }
  })

  return matches
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map(({ option }) => option)
}

/**
 * Rounds a number to a specified number of decimal places.
 *
 * @param {number} number - The number to round.
 * @param {number} decimals - The number of decimal places to round to.
 * @return {number} - The rounded number.
 */
export const roundToDecimal = (number: number, decimals: number) => {
  const multiplier = Math.pow(10, decimals || 0)
  return Math.round(number * multiplier) / multiplier
}

/**
 * Returns a formatted label for the given skill experience in months.
 *
 * @param {number} months - The number of months of skill experience.
 * @param {boolean} [abbreviation=false] - Indicates whether to use abbreviations for the label.
 * @return {string} The formatted label for the skill experience.
 */
export const getSkillExperienceLabel = (months: number, abbreviation = false) => {
  const monthSuffix = abbreviation ? i18n.t('profile.skill.month.abbr') : i18n.t('profile.skill.month')
  const monthsSuffix = abbreviation ? i18n.t('profile.skill.months.abbr') : i18n.t('profile.skill.months')
  const yearSuffix = abbreviation ? i18n.t('profile.skill.years.abbr') : i18n.t('profile.skill.year')
  const yearsSuffix = abbreviation ? i18n.t('profile.skill.years.abbr') : i18n.t('profile.skill.years')
  if (months < 1) {
    return `~ 1 ${monthSuffix}`
  } else if (months % 1 !== 0) {
    const value = Math.round(months)
    return `~ ${value} ${value === 1 ? monthSuffix : monthsSuffix}`
  } else {
    const fullYears = Math.floor(months / 12)
    const fullMonths = months % 12
    const yearsLabel = `${fullYears} ${fullYears === 1 ? yearSuffix : yearsSuffix}`
    const monthsLabel = `${fullMonths} ${fullMonths === 1 ? monthSuffix : monthsSuffix}`
    if (fullYears < 1) {
      return monthsLabel
    } else if (fullMonths < 1) {
      return yearsLabel
    } else {
      return `${yearsLabel} ${monthsLabel}`
    }
  }
}

/**
 * Converts a number to a positive integer.
 *
 * @param {number} number - The number to be converted.
 * @return {number} The converted positive integer value.
 */
export const toPositiveInteger = (number: number) => {
  return Math.abs(toInteger(number))
}

/**
 * A function that lightens or darkens a given color by a specified amount.
 *
 * @param {string} color - The color to be lightened or darkened.
 * @param {number} amount - The amount by which to lighten or darken the color.
 * @return {string} The modified color.
 */
export const lightenDarkenColor = function (color: string, amount: number) {
  let usePound = false

  if (color[0] == '#') {
    color = color.slice(1)
    usePound = true
  }

  const num = parseInt(color, 16)

  let r = (num >> 16) + amount

  if (r > 255) r = 255
  else if (r < 0) r = 0

  let b = ((num >> 8) & 0x00ff) + amount

  if (b > 255) b = 255
  else if (b < 0) b = 0

  let g = (num & 0x0000ff) + amount

  if (g > 255) g = 255
  else if (g < 0) g = 0

  return (usePound ? '#' : '') + padWithZeros((g | (b << 8) | (r << 16)).toString(16), 6)
}

const padWithZeros = (number: string, length: number) => {
  return number.padStart(length, '0')
}

/**
 * Validates an email address.
 *
 * @param {string} email - The email address to validate.
 * @return {boolean} Returns true if the email address is valid, false otherwise.
 */
export const validateEmail = (email: string) => {
  if (email.length < 5) return false

  const regExp =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

  return regExp.test(String(email).toLowerCase())
}

/**
 * Returns the color associated with a skill level.
 *
 * @param {number} level - The level of the skill.
 * @param {boolean} [light] - Whether to return a light color.
 * @return {string} - The color associated with the skill level.
 */
export const pickSkillColor = (level: number, light?: boolean) => {
  if (light) {
    switch (level) {
      case 1:
        return grey[100]

      case 2:
        return amber[50]

      case 3:
        return purple[50]

      case 4:
        return teal[50]

      case 5:
        return green[50]

      default:
        return grey[50]
    }
  }
  switch (level) {
    case 1:
      return grey[800]

    case 2:
      return deepOrange[900]

    case 3:
      return purple[700]

    case 4:
      return teal[700]

    case 5:
      return green[800]

    default:
      return grey[300]
  }
}

/**
 * Updates the date properties of the given item.
 *
 * @param {Object} item - the item to update
 * @returns {void}
 */
export const updateDate = (item: { startDate?: Date | string | null; endDate?: Date | string | null }) => {
  if (item.startDate && !(item.startDate instanceof Date)) item.startDate = new Date(item.startDate)
  if (item.endDate && !(item.endDate instanceof Date)) item.endDate = new Date(item.endDate)
}

/**
 * Generates a random color in the format 'rgb(r,g,b, 0.5)'.
 *
 * @return {string} The randomly generated color.
 */
export const randomColor = () => {
  const r = Math.floor(Math.random() * 255)
  const g = Math.floor(Math.random() * 255)
  const b = Math.floor(Math.random() * 255)
  return 'rgb(' + r + ',' + g + ',' + b + ', 0.5)'
}

/**
 * Remove non-Caleo storage items from local storage.
 *
 * @return {void} This function does not return anything.
 */
export const removeNonCaleoStorage = () => {
  const items = { ...localStorage }
  for (const item in items) {
    if (
      typeof item === 'string' &&
      !item.includes('Count') &&
      !item.includes('Columns') &&
      !item.includes('Page') &&
      !item.includes('Visibility') &&
      !item.includes('i18nextLng') &&
      !item.includes('azureRedirect') &&
      !item.includes('debug')
    ) {
      localStorage.removeItem(item)
    }
  }
}

/**
 * Returns an array of used application options.
 *
 * @return {Array<string>} - An array containing the names of the used application options.
 */
export const getUsedApplicationOptions = () => {
  return [
    'Netvisor',
    'Fennoa',
    'Procountor',
    'Fivaldi',
    'Heeros',
    'Asteri',
    'EmCe',
    'Passeli Merit',
    'Mepco',
    'Lemonsoft',
    'Palkkaus.fi',
    'Visma Payroll',
    'Sonet',
    'Maatalousneuvos',
  ]
}
